import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormControlLabel, Grid, Switch, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addGroupTicketDemand, editGroupTicketDemand } from '../../../api/endpoints/demands';
import type { GroupTicketDemand } from '../../../api/types/demands';
import { DemandFormDialog } from '../components/DemandFormDialog';

const schema = z.object({
  from: z.string().min(1, 'Kalkış yeri gerekli'),
  to: z.string().min(1, 'Varış yeri gerekli'),
  personCount: z.coerce.number().min(1, 'Kişi sayısı en az 1 olmalı'),
  depDate: z.string().min(1, 'Gidiş tarihi gerekli'),
  retDate: z.string().optional(),
  /** Comma-separated free text in the form; split into GroupTicketDemand.airlinePreference[]
   *  on submit. Must always be sent as an array (even empty) — see EditGroupTicketDemand's
   *  server-side null-ref quirk documented in api/types/demands.ts. */
  airlinePreferenceText: z.string().optional(),
  isOtelIncluded: z.boolean().optional(),
  isVisaIncluded: z.boolean().optional(),
  isTransferIncluded: z.boolean().optional(),
  isApproved: z.boolean().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  from: '',
  to: '',
  personCount: 1,
  depDate: '',
  retDate: '',
  airlinePreferenceText: '',
  isOtelIncluded: false,
  isVisaIncluded: false,
  isTransferIncluded: false,
  isApproved: false,
};

function toFormValues(demand: GroupTicketDemand): FormValues {
  return {
    from: demand.from ?? '',
    to: demand.to ?? '',
    personCount: demand.personCount ?? 1,
    depDate: demand.depDate ?? '',
    retDate: demand.retDate ?? '',
    airlinePreferenceText: (demand.airlinePreference ?? []).join(', '),
    isOtelIncluded: demand.isOtelIncluded ?? false,
    isVisaIncluded: demand.isVisaIncluded ?? false,
    isTransferIncluded: demand.isTransferIncluded ?? false,
    isApproved: demand.isApproved ?? false,
  };
}

/** Create/edit dialog for Grup Bilet Talebi. Edit also serves as the status-update action
 *  (the "Onaylandı" switch, wired to EditGroupTicketDemand's `isApproved` field). */
export function GroupTicketDemandDialog({
  open,
  demand,
  onClose,
}: {
  open: boolean;
  demand: GroupTicketDemand | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!demand;

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (open) reset(demand ? toFormValues(demand) : emptyValues);
  }, [open, demand, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const airlinePreference = (values.airlinePreferenceText ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const { airlinePreferenceText: _omit, ...rest } = values;
      return isEdit && demand?.id
        ? editGroupTicketDemand({ ...rest, id: demand.id, airlinePreference })
        : addGroupTicketDemand({ ...rest, airlinePreference });
    },
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Talep güncellendi.' : 'Talep oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['demands', 'groupTicket'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <DemandFormDialog
      open={open}
      title={isEdit ? 'Grup Bilet Talebini Güncelle' : 'Yeni Grup Bilet Talebi'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Kalkış (From)"
            error={!!errors.from}
            helperText={errors.from?.message}
            {...register('from')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Varış (To)"
            error={!!errors.to}
            helperText={errors.to?.message}
            {...register('to')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Kişi Sayısı"
            error={!!errors.personCount}
            helperText={errors.personCount?.message}
            slotProps={{ htmlInput: { min: 1 } }}
            {...register('personCount')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth size="small" label="Havayolu Tercihi (virgülle ayırın)" {...register('airlinePreferenceText')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Gidiş Tarihi"
            slotProps={{ inputLabel: { shrink: true } }}
            error={!!errors.depDate}
            helperText={errors.depDate?.message}
            {...register('depDate')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Dönüş Tarihi"
            slotProps={{ inputLabel: { shrink: true } }}
            {...register('retDate')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Controller
            control={control}
            name="isOtelIncluded"
            render={({ field }) => (
              <FormControlLabel
                control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                label="Otel Dahil"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Controller
            control={control}
            name="isVisaIncluded"
            render={({ field }) => (
              <FormControlLabel
                control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                label="Vize Dahil"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Controller
            control={control}
            name="isTransferIncluded"
            render={({ field }) => (
              <FormControlLabel
                control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                label="Transfer Dahil"
              />
            )}
          />
        </Grid>
        {isEdit && (
          <Grid size={{ xs: 12 }}>
            <Controller
              control={control}
              name="isApproved"
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Onaylandı"
                />
              )}
            />
          </Grid>
        )}
      </Grid>
    </DemandFormDialog>
  );
}
